import {FC, useEffect} from 'react'
import {Routes, Route, Navigate} from 'react-router-dom'
import {AdminAuthProvider, useAdminAuth} from './auth/AdminAuthContext'
import {AdminLogin} from './auth/AdminLogin'
import {AdminLayout} from './AdminLayout'
import TicketsPage from './tickets/TicketsPage'
import TicketDetailPage from './tickets/TicketDetailPage'
import AccountsPage from './accounts/AccountsPage'
import AccountDetailPage from './accounts/AccountDetailPage'
import LoginAsRedirect from './accounts/LoginAsRedirect'
import IssuesPage from './issues/IssuesPage'
import SchedulerPage from './scheduler/SchedulerPage'
import LibrariesPage from './libraries/LibrariesPage'
import LibraryDetailPage from './libraries/LibraryDetailPage'
import CampaignCategorizationPage from './campaign-categorization/CampaignCategorizationPage'
import SalesLibraryPage from './sales-library/SalesLibraryPage'
import SalesRecordingDetailPage from './sales-library/SalesRecordingDetailPage'
import SalesTrainingPage from './sales-library/SalesTrainingPage'
import MarketplacePage from './marketplace/MarketplacePage'
import AdminUsersPage from './admin-users/AdminUsersPage'
import MeetingsPage from './meetings/MeetingsPage'
import SubprofilesPage from './subprofiles/SubprofilesPage'
import ScheduledSubprofilesPage from './scheduled-subprofiles/ScheduledSubprofilesPage'
import DuplicateProfilesPage from './duplicate-profiles/DuplicateProfilesPage'
import AdminSettingsPage from './settings/AdminSettingsPage'
import PaymentsPage from './payments/PaymentsPage'
import PaymentBreakdownPage from './payment-breakdown/PaymentBreakdownPage'
import AffiliatePayoutsPage from './affiliate-payouts/AffiliatePayoutsPage'
import AdminReferralPromoPage from './referral-promo/AdminReferralPromoPage'
import SmsCampaignsPage from './sms-campaigns/SmsCampaignsPage'
import ApiKeysPage from './api-keys/ApiKeysPage'
import ReferrersPage from './referrers/ReferrersPage'
import VirtualNumbersPage from './virtual-numbers/VirtualNumbersPage'
import SalesAgentsPage from './sales-agents/SalesAgentsPage'
import AccountCitySearchPage from './account-city-search/AccountCitySearchPage'
import CurlToCookiesPage from './curl-to-cookies/CurlToCookiesPage'
import RequestLogsPage from './request-logs/RequestLogsPage'
import LoggedOutProfilesPage from './logged-out-profiles/LoggedOutProfilesPage'
import LoginAttemptsPage from './login-attempts/LoginAttemptsPage'
import PostPacingPage from './post-pacing/PostPacingPage'
import PostConfirmationsPage from './post-confirmations/PostConfirmationsPage'
import WhatsappPage from './whatsapp-inbox/WhatsappPage'
import WhatsappGridPage from './whatsapp-inbox/WhatsappGridPage'
import AdsPage from './ads/AdsPage'

// Hide the main app splash screen for admin routes
const HideSplashScreen: FC = () => {
  useEffect(() => {
    const splashScreen = document.getElementById('splash-screen')
    if (splashScreen) {
      splashScreen.style.display = 'none'
    }
  }, [])
  return null
}

// Protected route wrapper
const ProtectedAdminRoute: FC<{children: React.ReactNode}> = ({children}) => {
  const {admin, loading} = useAdminAuth()

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{minHeight: '100vh'}}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    )
  }

  if (!admin) {
    return <Navigate to="/admin/login" replace />
  }

  return <>{children}</>
}

// Admin routes content (inside provider)
const AdminRoutesContent: FC = () => {
  const {admin, loading} = useAdminAuth()

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{minHeight: '100vh'}}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    )
  }

  return (
    <Routes>
      {/* Public admin routes */}
      <Route
        path="login"
        element={admin ? <Navigate to="/admin/tickets" replace /> : <AdminLogin />}
      />

      {/* Protected admin routes */}
      <Route
        element={
          <ProtectedAdminRoute>
            <AdminLayout />
          </ProtectedAdminRoute>
        }
      >
        <Route path="tickets" element={<TicketsPage />} />
        <Route path="tickets/:ticketId" element={<TicketDetailPage />} />
        <Route path="accounts" element={<AccountsPage />} />
        <Route path="accounts/:id" element={<AccountDetailPage />} />
        <Route path="login-as/:id" element={<LoginAsRedirect />} />
        <Route path="issues" element={<IssuesPage />} />
        <Route path="scheduler" element={<SchedulerPage />} />
        <Route path="libraries" element={<LibrariesPage />} />
        <Route path="libraries/:id" element={<LibraryDetailPage />} />
        <Route path="campaign-categorization" element={<CampaignCategorizationPage />} />
        <Route path="sales-library" element={<SalesLibraryPage />} />
        <Route path="sales-library/:id" element={<SalesRecordingDetailPage />} />
        <Route path="sales-training" element={<SalesTrainingPage />} />
        <Route path="marketplace" element={<MarketplacePage />} />
        <Route path="admin-users" element={<AdminUsersPage />} />
        <Route path="meetings" element={<MeetingsPage />} />
        <Route path="virtual-numbers" element={<VirtualNumbersPage />} />
        <Route path="subprofiles" element={<SubprofilesPage />} />
        <Route path="scheduled-subprofiles" element={<ScheduledSubprofilesPage />} />
        <Route path="duplicate-profiles" element={<DuplicateProfilesPage />} />
        <Route path="settings" element={<AdminSettingsPage />} />
        <Route path="payments" element={<PaymentsPage />} />
        <Route path="payment-breakdown" element={<PaymentBreakdownPage />} />
        <Route path="affiliate-payouts" element={<AffiliatePayoutsPage />} />
        <Route path="referral-promo" element={<AdminReferralPromoPage />} />
        <Route path="sms-campaigns" element={<SmsCampaignsPage />} />
        <Route path="api-keys" element={<ApiKeysPage />} />
        <Route path="referrers" element={<ReferrersPage />} />
        <Route path="sales-agents" element={<SalesAgentsPage />} />
        <Route path="account-city-search" element={<AccountCitySearchPage />} />
        <Route path="curl-to-cookies" element={<CurlToCookiesPage />} />
        <Route path="request-logs" element={<RequestLogsPage />} />
        <Route path="logged-out-profiles" element={<LoggedOutProfilesPage />} />
        <Route path="login-attempts" element={<LoginAttemptsPage />} />
        <Route path="post-pacing" element={<PostPacingPage />} />
        <Route path="post-confirmations" element={<PostConfirmationsPage />} />
        <Route path="whatsapp" element={<WhatsappPage />} />
        <Route path="whatsapp-grid" element={<WhatsappGridPage />} />
        <Route path="ads" element={<AdsPage />} />
      </Route>

      {/* Default redirect */}
      <Route path="*" element={<Navigate to="/admin/tickets" replace />} />
    </Routes>
  )
}

// Main admin routes component with provider
export const AdminRoutes: FC = () => {
  return (
    <>
      <HideSplashScreen />
      <AdminAuthProvider>
        <AdminRoutesContent />
      </AdminAuthProvider>
    </>
  )
}
